import { bopApi } from '@/lib/api/client';

export const SURVEILLANCE_ALERTS_PATH = 'v1/market-surveillance/alerts';

export const SURVEILLANCE_STATUSES = ['OPEN', 'INVESTIGATING', 'ESCALATED', 'CLOSED_FP', 'CLOSED_TP', 'CLOSED_ADJUDICATED', 'STOR_FILED'] as const;
export type SurveillanceStatus = (typeof SURVEILLANCE_STATUSES)[number];

export type SurveillanceSortField = 'createdAt' | 'riskScore';

export const SURVEILLANCE_CLIENT_TYPES = ['MARKET_MAKER', 'RETAIL'] as const;
export type SurveillanceClientType = (typeof SURVEILLANCE_CLIENT_TYPES)[number];

/** actionable = the alerts narc posts to Slack; all adds the suppressed and low-triage alerts it also stores */
export type SurveillanceAlertScope = 'actionable' | 'all';

/** Raw narc alert row from POST /v1/market-surveillance/alerts */
export interface RawSurveillanceAlertRow {
  alert_id: string;
  /** ISO-8601 */
  created_at: string;
  typology: string;
  /** 0–100 int */
  risk_score: number;
  /** 0–1 float */
  triage_score: number;
  instrument: string;
  uid: number;
  ai: number;
  pid: string | null;
  /** null = never triaged (no status row yet) */
  status: string | null;
  assigned_to: string | null;
  case_id: string;
  summary: string;
  client_type?: string;
  /** Matched suppression rule; null when the alert was not suppressed */
  suppressed_by?: string | null;
  /** Whether narc posted (or would post) this alert to Slack */
  actionable?: boolean;
}

export interface SurveillanceAlertRow {
  alertId: string;
  createdAt: string;
  typology: string;
  riskScore: number;
  triageScore: number;
  instrument: string;
  uid: number;
  ai: number;
  pid: string | null;
  status: SurveillanceStatus;
  assignedTo: string | null;
  caseId: string;
  summary: string;
  clientType: SurveillanceClientType;
  suppressedBy: string | null;
  actionable: boolean;
}

export interface RawSurveillanceAlertsResponse {
  alerts: RawSurveillanceAlertRow[];
  cursor?: string;
}

export interface SurveillanceAlertsPage {
  alerts: SurveillanceAlertRow[];
  cursor?: string;
}

export interface SurveillanceAlertFilters {
  alertId?: string;
  pid?: string;
  typology?: string[];
  status?: SurveillanceStatus[];
  riskScoreMin?: number;
  riskScoreMax?: number;
  /** ISO-8601 bounds on created_at */
  from?: string;
  to?: string;
  instrument?: string[];
  sort?: SurveillanceSortField;
  scope?: SurveillanceAlertScope;
  clientType?: SurveillanceClientType[];
}

export interface SurveillanceAlertsSearchRequest {
  cursor?: string;
  alertId?: string;
  pid?: string;
  typology?: string[];
  status?: string[];
  riskScoreMin?: number;
  riskScoreMax?: number;
  from?: string;
  to?: string;
  instrument?: string[];
  sort?: SurveillanceSortField;
  scope?: SurveillanceAlertScope;
  clientType?: SurveillanceClientType[];
}

export interface RawSurveillanceAlertFull extends RawSurveillanceAlertRow {
  narrative: string | null;
  parameters_used: Record<string, unknown> | null;
  actuals: Record<string, unknown> | null;
  alert_start: string | null;
  alert_end: string | null;
  counterparty_uid: number | null;
}

export interface RawSurveillanceEvidenceRef {
  ref_kind: 'order' | 'trade';
  ref_id: string;
}

export interface RawSurveillanceAlertDetail {
  alert: RawSurveillanceAlertFull;
  status: { status: string | null; assigned_to: string | null; updated_at: string | null };
  /** Always derived server-side from the alerts.evidence JSON */
  evidence?: RawSurveillanceEvidenceRef[];
  can_edit?: boolean;
  can_reopen?: boolean;
  allowed_transitions?: string[];
}

export interface SurveillanceAlertFull extends SurveillanceAlertRow {
  narrative: string | null;
  parametersUsed: Record<string, unknown> | null;
  actuals: Record<string, unknown> | null;
  alertStart: string | null;
  alertEnd: string | null;
  counterpartyUid: number | null;
}

export interface SurveillanceEvidenceRef {
  refKind: 'order' | 'trade';
  refId: string;
}

export interface SurveillanceAlertDetail {
  alert: SurveillanceAlertFull;
  status: { status: SurveillanceStatus; assignedTo: string | null; updatedAt: string | null };
  evidence: SurveillanceEvidenceRef[];
  canEdit: boolean;
  canReopen: boolean;
  /** Server-driven state machine — the UI renders exactly these, never computes transitions itself */
  allowedTransitions: SurveillanceStatus[];
}

export interface RawSurveillanceAuditEntry {
  /** ISO-8601 */
  ts: string;
  event: string;
  status: string | null;
  assigned_to: string | null;
  performed_by: string | null;
  notes: string | null;
  old_value: string | null;
  new_value: string | null;
}

export interface RawSurveillanceAuditResponse {
  entries: RawSurveillanceAuditEntry[];
}

export interface SurveillanceAuditEntry {
  ts: string;
  event: string;
  status: string | null;
  assignedTo: string | null;
  performedBy: string | null;
  notes: string | null;
  oldValue: string | null;
  newValue: string | null;
}

export interface RawSurveillanceParticipantHistory {
  total_alerts: number;
  open: number;
  investigating: number;
  escalated: number;
  closed_fp: number;
  closed_tp: number;
  closed_adjudicated: number;
  stor_filed: number;
}

export interface RawSurveillanceRelatedResponse {
  related_alerts: RawSurveillanceAlertRow[];
  participant_history: RawSurveillanceParticipantHistory;
}

export interface SurveillanceParticipantHistory {
  totalAlerts: number;
  open: number;
  investigating: number;
  escalated: number;
  closedFp: number;
  closedTp: number;
  closedAdjudicated: number;
  storFiled: number;
}

export interface SurveillanceRelated {
  relatedAlerts: SurveillanceAlertRow[];
  participantHistory: SurveillanceParticipantHistory;
}

// Never-triaged rows have no status row (null), and a newer narc may ship statuses this
// build doesn't know — both degrade to OPEN rather than crashing the queue.
const toSurveillanceStatus = (value: string | null | undefined): SurveillanceStatus =>
  SURVEILLANCE_STATUSES.includes(value as SurveillanceStatus) ? (value as SurveillanceStatus) : 'OPEN';

// Retail is the safe reading for anything this build doesn't recognise: it is the class with the
// tighter surveillance thresholds, and the server only ever sends the two known values.
const toClientType = (value: string | undefined): SurveillanceClientType =>
  SURVEILLANCE_CLIENT_TYPES.includes(value as SurveillanceClientType) ? (value as SurveillanceClientType) : 'RETAIL';

const knownStatuses = (values: string[] | undefined): SurveillanceStatus[] =>
  (values ?? []).filter((value): value is SurveillanceStatus => SURVEILLANCE_STATUSES.includes(value as SurveillanceStatus));

export const mapAlertRow = (raw: RawSurveillanceAlertRow): SurveillanceAlertRow => ({
  alertId: raw.alert_id,
  createdAt: raw.created_at,
  typology: raw.typology,
  riskScore: raw.risk_score,
  triageScore: raw.triage_score,
  instrument: raw.instrument,
  uid: raw.uid,
  ai: raw.ai,
  pid: raw.pid,
  status: toSurveillanceStatus(raw.status),
  assignedTo: raw.assigned_to,
  caseId: raw.case_id,
  summary: raw.summary,
  clientType: toClientType(raw.client_type),
  suppressedBy: raw.suppressed_by ?? null,
  // An API that predates the flag only ever returned what is now the actionable scope's superset;
  // treating unknown as actionable avoids marking every row "not posted"
  actionable: raw.actionable ?? true,
});

export const mapAlertsResponse = (raw: RawSurveillanceAlertsResponse): SurveillanceAlertsPage => ({
  alerts: (raw.alerts ?? []).map(mapAlertRow),
  cursor: raw.cursor,
});

// Capabilities default FALSE (read-only fail-safe) and unknown transitions are dropped —
// a stale UI must never offer a transition the server would reject.
export const mapAlertDetail = (raw: RawSurveillanceAlertDetail): SurveillanceAlertDetail => ({
  alert: {
    ...mapAlertRow(raw.alert),
    narrative: raw.alert.narrative,
    parametersUsed: raw.alert.parameters_used,
    actuals: raw.alert.actuals,
    alertStart: raw.alert.alert_start,
    alertEnd: raw.alert.alert_end,
    counterpartyUid: raw.alert.counterparty_uid,
  },
  status: {
    status: toSurveillanceStatus(raw.status.status),
    assignedTo: raw.status.assigned_to,
    updatedAt: raw.status.updated_at,
  },
  evidence: (raw.evidence ?? []).map(({ ref_kind, ref_id }) => ({ refKind: ref_kind, refId: ref_id })),
  canEdit: raw.can_edit ?? false,
  canReopen: raw.can_reopen ?? false,
  allowedTransitions: knownStatuses(raw.allowed_transitions),
});

// Newest first — server order is insertion order, not guaranteed
export const mapAuditEntries = (raw: RawSurveillanceAuditResponse): SurveillanceAuditEntry[] =>
  [...(raw.entries ?? [])]
    .sort((a, b) => Date.parse(b.ts) - Date.parse(a.ts))
    .map((entry) => ({
      ts: entry.ts,
      event: entry.event,
      status: entry.status,
      assignedTo: entry.assigned_to,
      performedBy: entry.performed_by,
      notes: entry.notes,
      oldValue: entry.old_value,
      newValue: entry.new_value,
    }));

export const mapAlertRelated = (raw: RawSurveillanceRelatedResponse): SurveillanceRelated => ({
  relatedAlerts: (raw.related_alerts ?? []).map(mapAlertRow),
  participantHistory: {
    totalAlerts: raw.participant_history.total_alerts,
    open: raw.participant_history.open,
    investigating: raw.participant_history.investigating,
    escalated: raw.participant_history.escalated,
    closedFp: raw.participant_history.closed_fp,
    closedTp: raw.participant_history.closed_tp,
    closedAdjudicated: raw.participant_history.closed_adjudicated,
    storFiled: raw.participant_history.stor_filed,
  },
});

export const buildAlertsSearchRequest = (filters: SurveillanceAlertFilters, cursor?: string): SurveillanceAlertsSearchRequest => ({
  ...(cursor ? { cursor } : {}),
  ...(filters.alertId ? { alertId: filters.alertId } : {}),
  ...(filters.pid ? { pid: filters.pid } : {}),
  ...(filters.typology?.length ? { typology: filters.typology } : {}),
  ...(filters.status?.length ? { status: filters.status } : {}),
  ...(filters.riskScoreMin !== undefined ? { riskScoreMin: filters.riskScoreMin } : {}),
  ...(filters.riskScoreMax !== undefined ? { riskScoreMax: filters.riskScoreMax } : {}),
  ...(filters.from ? { from: filters.from } : {}),
  ...(filters.to ? { to: filters.to } : {}),
  ...(filters.instrument?.length ? { instrument: filters.instrument } : {}),
  ...(filters.sort ? { sort: filters.sort } : {}),
  ...(filters.scope ? { scope: filters.scope } : {}),
  ...(filters.clientType?.length ? { clientType: filters.clientType } : {}),
});

export const getSurveillanceAlerts = (request: SurveillanceAlertsSearchRequest, signal?: AbortSignal) =>
  bopApi.post(SURVEILLANCE_ALERTS_PATH, { json: request, signal }).json<RawSurveillanceAlertsResponse>();

export const getSurveillanceAlert = (alertId: string) => bopApi.get(`${SURVEILLANCE_ALERTS_PATH}/${alertId}`).json<RawSurveillanceAlertDetail>();

export const getAlertAudit = (alertId: string) => bopApi.get(`${SURVEILLANCE_ALERTS_PATH}/${alertId}/audit`).json<RawSurveillanceAuditResponse>();

export const getAlertRelated = (alertId: string) => bopApi.get(`${SURVEILLANCE_ALERTS_PATH}/${alertId}/related`).json<RawSurveillanceRelatedResponse>();

export interface ChangeAlertStatusRequest {
  status: SurveillanceStatus;
  /** Free-text assessment recorded in the audit trail alongside the transition */
  assessment: string;
  assignedTo?: string;
}

export interface RawChangeAlertStatusResponse {
  alert_id: string;
  old_status: string;
  new_status: string;
  performed_by: string;
  /** Next legal transitions for the NEW status — lets the client patch caches without refetching */
  allowed_transitions: string[];
}

// Post-write cache patchers. After a write the client patches from the response instead of
// refetching: bop-api writes via the primary but reads via the replica, so an immediate
// refetch races replication and pins the OLD status for the whole staleTime.

export const patchRawDetailAfterStatusChange = (raw: RawSurveillanceAlertDetail | undefined, response: RawChangeAlertStatusResponse): RawSurveillanceAlertDetail | undefined =>
  raw
    ? {
        ...raw,
        status: { ...raw.status, status: response.new_status, updated_at: new Date().toISOString() },
        // Preserved when a server omits the field — never blank the action menu on a patch
        allowed_transitions: response.allowed_transitions ?? raw.allowed_transitions,
      }
    : raw;

export const syntheticStatusAuditEntry = (response: RawChangeAlertStatusResponse, assessment: string): RawSurveillanceAuditEntry => ({
  ts: new Date().toISOString(),
  event: response.new_status === 'ESCALATED' ? 'ESCALATED' : 'STATUS_CHANGE',
  status: response.new_status,
  assigned_to: null,
  performed_by: response.performed_by,
  notes: assessment,
  old_value: response.old_status,
  new_value: response.new_status,
});

export const syntheticNoteAuditEntry = (response: RawAddAlertNoteResponse, note: string): RawSurveillanceAuditEntry => ({
  ts: new Date().toISOString(),
  event: 'NOTE',
  status: response.status,
  assigned_to: null,
  performed_by: response.performed_by,
  notes: note,
  old_value: null,
  new_value: null,
});

export const prependRawAuditEntry = (raw: RawSurveillanceAuditResponse | undefined, entry: RawSurveillanceAuditEntry): RawSurveillanceAuditResponse | undefined => {
  if (!raw) return raw;
  const entries = raw.entries ?? [];
  // The audit list re-sorts by ts, so a lagging client clock would sink the just-performed
  // action below older entries — pin the synthetic entry just past the newest known one.
  const maxExistingMs = entries.reduce((max, existing) => Math.max(max, new Date(existing.ts).getTime() || 0), 0);
  const ts = new Date(Math.max(Date.now(), maxExistingMs + 1)).toISOString();
  return { entries: [{ ...entry, ts }, ...entries] };
};

export const patchRawListRowStatus = (raw: RawSurveillanceAlertsResponse | undefined, alertId: string, newStatus: string): RawSurveillanceAlertsResponse | undefined =>
  raw ? { ...raw, alerts: (raw.alerts ?? []).map((row) => (row.alert_id === alertId ? { ...row, status: newStatus } : row)) } : raw;

export interface RawAddAlertNoteResponse {
  alert_id: string;
  status: string;
  performed_by: string;
}

// Write errors carry enum codes in { error: string }: 409 REVIEW_IN_PROGRESS (retryable),
// 400 ILLEGAL_TRANSITION, 404 ALERT_NOT_FOUND — map codes to copy in the UI layer.
export const changeAlertStatus = (alertId: string, body: ChangeAlertStatusRequest) =>
  bopApi.put(`${SURVEILLANCE_ALERTS_PATH}/${alertId}/status`, { json: body }).json<RawChangeAlertStatusResponse>();

export const addAlertNote = (alertId: string, body: { note: string }) =>
  bopApi.post(`${SURVEILLANCE_ALERTS_PATH}/${alertId}/notes`, { json: body }).json<RawAddAlertNoteResponse>();
